import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { RQ_BAG_BASIC_KEY, RQ_BAG_DETAILED_KEY } from '../ReactQuery/cacheKeys';
import { type CurrencyCode } from '../currencies/constants';
import { useCurrentLanguage } from '../language/useCurrentLanguage';

import { type DetailedBag, type PatchBagRequest } from './aliases';
import { updateAuthBag } from './api';
import {
  generateOptimisticDetailedBagSellers,
  updateGuestBag,
} from './helpers';
import { type BagBasicQueryResult, type OptimisticDetailedBag } from './types';
import { useBagBasic } from './useBagBasic';

import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useAddToBag(currency?: CurrencyCode) {
  const t = useTranslations('bag');
  const queryClient = useQueryClient();
  const { language } = useCurrentLanguage();
  const [authenticated] = useCurrentUser();
  const [errorText, setErrorText] = useState('');
  useBagBasic();

  function onSettled() {
    queryClient.invalidateQueries({ queryKey: [RQ_BAG_BASIC_KEY] });
    queryClient.invalidateQueries({
      queryKey: [RQ_BAG_DETAILED_KEY, language],
    });
  }

  function onError() {
    setErrorText(t('Error.AddToBag'));
  }

  const addToBagMutation = useMutation({
    mutationFn: updateAuthBag,

    onMutate: async (mutationData: PatchBagRequest) => {
      await queryClient.cancelQueries({
        queryKey: [RQ_BAG_BASIC_KEY],
      });

      // Snapshot the previous value
      const previousBasicBagData =
        queryClient.getQueryData<BagBasicQueryResult>([RQ_BAG_BASIC_KEY]);

      const previousDetailedBagData = queryClient.getQueryData<DetailedBag>([
        RQ_BAG_DETAILED_KEY,
        language,
      ]) || { sellers: [] };

      const [request] = mutationData;

      if (previousBasicBagData) {
        // Optimistically update to the new value
        queryClient.setQueryData<BagBasicQueryResult>([RQ_BAG_BASIC_KEY], {
          ...previousBasicBagData,
          count: previousBasicBagData?.count
            ? Number(previousBasicBagData.count) + 1
            : 1,
          products: {
            ...previousBasicBagData.products,
            [request.product_id]: [
              ...(previousBasicBagData.products[request.product_id] || []),
              {
                product_id: request.product_id,
                variant_id: request.variant_id,
              },
            ],
          },
        });
      }

      if (previousDetailedBagData && currency) {
        // Optimistically update
        const newSellers = generateOptimisticDetailedBagSellers({
          bagCache: previousDetailedBagData,
          request,
          currency,
        });

        queryClient.setQueriesData<OptimisticDetailedBag>(
          { queryKey: [RQ_BAG_DETAILED_KEY, language] },
          {
            sellers: newSellers,
          }
        );
      }

      // Return a context object with the snapshotted value
      return { previousBasicBagData, previousDetailedBagData };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_err, _newTodo, context) => {
      onError();

      if (context?.previousBasicBagData) {
        queryClient.setQueryData(
          [RQ_BAG_BASIC_KEY],
          context.previousBasicBagData
        );
      }

      if (context?.previousDetailedBagData) {
        queryClient.setQueriesData(
          { queryKey: [RQ_BAG_DETAILED_KEY, language] },
          context.previousDetailedBagData
        );
      }
    },
    // Always refetch after error or success:
    onSettled,
  });

  function addToBag(data: PatchBagRequest) {
    setErrorText('');

    if (authenticated) {
      addToBagMutation.mutate(data);
    } else {
      updateGuestBag(data, {
        onSettled,
        onError,
      });
    }
  }

  return { addToBag, errorText };
}
